import React from "react";
import DataTable from "react-data-table-component";
import DataTableExtensions from "react-data-table-component-extensions";
import "react-data-table-component-extensions/dist/index.css";
import Button from "@mui/material/Button";
import useStyles from "../dashAuction.style";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import eventBuyerListAPI from "../../../../api/adminAPI/eventBuyerListAPI";
import { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import moment from "moment";
import { Box } from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AuctionViewFirst from "./AuctionViewFirst";
import AuctionSummary from "./AuctionSummary";

import { FaRegFileExcel } from "react-icons/fa";
import * as XLSX from "xlsx";
import eventDetailsAPI from "../../../../api/adminAPI/eventDetailsAPI";
import BidderCompanyCell from "../../../commonComponents/BidderCompanyCell";
import { getTimeZone, getToken, localTimeZone } from "../../../../common";
import { BID_TIME_DISPLAY } from "../../../../common/dateTime";
import Tooltip from "@mui/material/Tooltip";
import RefreshIcon from "@mui/icons-material/Refresh";
import io from "socket.io-client";
import config from "../../../../config/config";
import eventBuyersPresenceAPI from "../../../../api/adminAPI/eventBuyersPresenceAPI";

// the bidder's live status: a dot before their name
const PRESENCE_LOOK = {
  ON_AUCTION: { color: "#22c55e", label: "On this auction (page open or bid in last 5 min)" },
  OFFLINE: { color: "#bdbdbd", label: "Not on this auction" },
};

const PresenceDot = ({ presence, zone }) => {
  const look = PRESENCE_LOOK[presence?.state] || PRESENCE_LOOK.OFFLINE;
  const when = (at) => moment(at).tz(zone).format(BID_TIME_DISPLAY);
  const detail = joined(
    [
      look.label,
      presence?.openSince && `open since ${when(presence.openSince)}`,
      presence?.lastBidAt && `last bid ${when(presence.lastBidAt)}`,
      presence?.ip && `IP ${presence.ip}`,
    ],
    " · "
  );
  return (
    <Tooltip title={detail}>
      <span
        aria-label={detail}
        style={{
          display: "inline-block",
          flexShrink: 0,
          width: 10,
          height: 10,
          borderRadius: "50%",
          marginRight: 8,
          backgroundColor: look.color,
        }}
      />
    </Tooltip>
  );
};

const joined = (parts, separator = ", ") =>
  parts.filter(Boolean).join(separator);

// Terms Status, in the chosen time zone, e.g.
//   Accepted · 15/10/2026 01:36:22 PM · IP 103.21.45.10
//   Not accepted · company details filled · <when saved at Next> · IP …
//   Not accepted · opened auction (3 times) · first <when> · IP … · last …
//   Not accepted · logged in, auction not opened · <last login> · IP …
//   Not accepted · no activity
// (logins and visits count from the auction's publish time to its end)
const termsStatusText = (row, zone) => {
  const when = (at) => (at ? moment(at).tz(zone).format(BID_TIME_DISPLAY) : "");
  const ip = (address) => (address ? `IP ${address}` : "");

  if (row.acceptedTerms) {
    return joined(
      ["Accepted", when(row.company?.acceptedAt), ip(row.termsIpAddress)],
      " · "
    );
  }
  if (row.company) {
    return joined(
      [
        "Not accepted · company details filled",
        when(row.detailsSavedAt),
        ip(row.termsIpAddress),
      ],
      " · "
    );
  }

  const visits = row.activity?.auctionVisits;
  if (visits) {
    const times = `${visits.count} ${visits.count === 1 ? "time" : "times"}`;
    if (visits.count === 1) {
      return joined(
        [
          `Not accepted · opened auction (${times})`,
          when(visits.first.at),
          ip(visits.first.ip),
        ],
        " · "
      );
    }
    return joined(
      [
        `Not accepted · opened auction (${times})`,
        `first ${when(visits.first.at)}`,
        ip(visits.first.ip),
        `last ${when(visits.last.at)}`,
        ip(visits.last.ip),
      ],
      " · "
    );
  }

  const login = row.activity?.lastLogin;
  if (login) {
    return joined(
      [
        "Not accepted · logged in, auction not opened",
        when(login.at),
        ip(login.ip),
      ],
      " · "
    );
  }
  return "Not accepted · no activity";
};

// everything the bidder gave: registration, and the company form filled
// before the terms (saved at its Next)
const BidderDetailsCell = ({ row, zone }) => {
  const company = row.company;
  const lines = [
    ["Registration", joined([row.email, row.mobile], " · ")],
    company && [
      "Contact",
      joined([
        company.contactName,
        company.designation && `(${company.designation})`,
      ], " "),
    ],
    company && [
      "Phone / Email",
      joined([company.contactPhone, company.contactEmail], " · "),
    ],
    company && [
      "Address",
      joined([company.city, joined([company.state, company.pinCode], " ")]),
    ],
    company && [
      "Tax",
      joined([
        company.gstin && `GSTIN ${company.gstin}`,
        company.pan && `PAN ${company.pan}`,
      ], " · "),
    ],
  ].filter((line) => line && line[1]);

  return (
    <Box sx={{ py: 0.5, fontSize: "12px", lineHeight: 1.5 }}>
      {lines.map(([label, text]) => (
        <div key={label}>
          <span style={{ color: "#6c757d" }}>{label}: </span>
          {text}
        </div>
      ))}
    </Box>
  );
};

function BidlogFirst({
  DashBuyerDetail,
  AuctionView,
  eventId,
  AuctionViewFirst,
  AuctionSummary,
}) {
  const classes = useStyles();
  const zone = getTimeZone() || localTimeZone();

  const [tableEventBuyersData, setTableEventBuyersData] = useState([]);
  const [eventType, setEventType] = useState("");
  const [presence, setPresence] = useState({});
  const [checkingPresence, setCheckingPresence] = useState(false);

  // live status: checked when the list opens, on "Refresh live status", when the
  // tab comes back to the front and when the internet comes back; nothing
  // runs on a timer
  const checkPresence = async () => {
    setCheckingPresence(true);
    try {
      const { data } = await eventBuyersPresenceAPI(eventId);
      setPresence(data || {});
    } catch (error) {
      console.log(error);
    } finally {
      setCheckingPresence(false);
    }
  };

  useEffect(() => {
    checkPresence();
    const onVisible = () => {
      if (document.visibilityState === "visible") checkPresence();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", checkPresence);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", checkPresence);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // a bidder who bids is on the auction: the server says who, and the dot
  // turns green without another check (one connection while this list is open)
  useEffect(() => {
    const listener = io(config.api.socket_url, {
      extraHeaders: { authorization: getToken() },
    });
    listener.on("connect", () => listener.emit("watch-presence", eventId));
    listener.on("bidder-bid", ({ userId, at }) =>
      setPresence((current) => ({
        ...current,
        [userId]: { ...current[userId], state: "ON_AUCTION", at, lastBidAt: at },
      }))
    );
    return () => listener.disconnect();
  }, [eventId]);

  const getEventBuyers = async (eventId) => {
    try {
      const { data: getEventsDetails } = await eventDetailsAPI(eventId);
      const { data } = await eventBuyerListAPI(eventId);

      if (data) {
        let id = 1;
        const buyersData = data.map((data, index) => {
          return {
            id: id++,
            userName: `${data.user.firstName} ${data.user.lastName}`,
            email: data.user.email,
            mobile: data.user.contactNumber
              ? String(data.user.contactNumber)
              : "",
            company: data.company,
            duplicateCompany: data.duplicateCompany,
            acceptedTerms: data.acceptedTerms,
            termsIpAddress: data.termsIpAddress,
            detailsSavedAt: data.detailsSavedAt,
            activity: data.activity,
            allotedLots: data.allotedLots,
            bidedLots: data.bidedLots,
            h1RanksCount: data.h1RanksCount,
            BuyerId: data.user.userId,
            h2RanksCount: data.h2RanksCount,
            h3RanksCount: data.h3RanksCount,
          };
        });

        setEventType(getEventsDetails.eventType);
        setTableEventBuyersData(buyersData);
      } else throw new Error(data);
    } catch (error) {
      console.log(error);
    }
  };

  React.useEffect(() => {
    getEventBuyers(eventId);
    return () => false;
  }, [eventId]);

  const nextColumns = [
    {
      name: "Sr.",
      selector: (row) => row.id, // Ensures proper numeric sorting
      cell: (data) => String(data.id),
      sortable: true,
    },
    {
      name: "Buyer",
      selector: (row) => row.userName?.toLowerCase() || "", // Case-insensitive sorting
      cell: (data) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PresenceDot presence={presence[data.BuyerId]} zone={zone} />
          {String(data.userName)}
        </Box>
      ),
      sortable: true,
    },
    {
      name: "Company",
      selector: (row) => row.company?.companyName?.toLowerCase() || "",
      cell: (row) => (
        <BidderCompanyCell
          company={row.company}
          duplicateCompany={row.duplicateCompany}
          acceptedTerms={row.acceptedTerms}
        />
      ),
      sortable: true,
      minWidth: "220px",
    },
    {
      name: "Terms Status",
      selector: (row) => termsStatusText(row, zone),
      cell: (row) => (
        <Box
          sx={{
            py: 0.5,
            fontSize: "12px",
            color: row.acceptedTerms ? "success.main" : "warning.main",
          }}
        >
          {termsStatusText(row, zone)}
        </Box>
      ),
      sortable: true,
      minWidth: "200px",
    },
    {
      name: "Bidder & Company Details",
      cell: (row) => <BidderDetailsCell row={row} zone={zone} />,
      minWidth: "320px",
    },
    {
      name: "Alloted Lots",
      selector: (row) => Number(row.allotedLots) || 0, // Ensures numeric sorting
      cell: (data) => String(data.allotedLots),
      sortable: true,
    },
    {
      name: "Bidded Lots",
      selector: (row) => Number(row.bidedLots) || 0, // Ensures numeric sorting
      cell: (data) => String(data.bidedLots),
      sortable: true,
    },
    {
      name: eventType === "FORWARD_AUCTION" ? "H1" : "L1",
      selector: (row) => Number(row.h1RanksCount) || 0, // Ensures numeric sorting
      cell: (data) => String(data.h1RanksCount),
      sortable: true,
    },
    {
      name: eventType === "FORWARD_AUCTION" ? "H2" : "L2",
      selector: (row) => Number(row.h2RanksCount) || 0, // Ensures numeric sorting
      cell: (data) => String(data.h2RanksCount),
      sortable: true,
    },
    {
      name: "Others",
      selector: (row) => Number(row.h3RanksCount) || 0, // Ensures numeric sorting
      cell: (data) => String(data.h3RanksCount),
      sortable: true,
    },
    {
      name: "Action",
      sortable: false, // Sorting is unnecessary for actions
      width: "14%",
      cell: (data) => (
        <Box sx={{ display: "flex" }}>
          <Button
            className="btn btn-group"
            onClick={() => {
              AuctionSummary(data.BuyerId);
            }}
          >
            <VisibilityIcon />
          </Button>
        </Box>
      ),
    },
  ];

  // one column per detail, so the file can be sorted and filtered; headers
  // and values come from the same list, so they can't shift
  const exportToXLSX = (tableData) => {
    const rank = eventType === "REVERSE_AUCTION" ? "L" : "H";
    const columns = [
      ["Sr.", (row) => row.id],
      ["Buyer", (row) => row.userName],
      ["Registration Email", (row) => row.email || ""],
      ["Registration Mobile", (row) => row.mobile || ""],
      ["Company", (row) => row.company?.companyName || ""],
      ["GSTIN", (row) => row.company?.gstin || ""],
      ["PAN", (row) => row.company?.pan || ""],
      ["Contact Name", (row) => row.company?.contactName || ""],
      ["Designation", (row) => row.company?.designation || ""],
      ["Contact Phone", (row) => row.company?.contactPhone || ""],
      ["Contact Email", (row) => row.company?.contactEmail || ""],
      ["City", (row) => row.company?.city || ""],
      ["State", (row) => row.company?.state || ""],
      ["PIN", (row) => row.company?.pinCode || ""],
      ["Terms Status", (row) => termsStatusText(row, zone)],
      ["Alloted Lots", (row) => Number(row.allotedLots) || 0],
      ["Bidded Lots", (row) => Number(row.bidedLots) || 0],
      [`${rank}1`, (row) => Number(row.h1RanksCount) || 0],
      [`${rank}2`, (row) => Number(row.h2RanksCount) || 0],
      ["Others", (row) => Number(row.h3RanksCount) || 0],
    ];
    const header = columns.map(([name]) => name);
    const rows = tableData.map((row) => columns.map(([, value]) => value(row)));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);

    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, "buyer-event-summary.xlsx");
  };

  const ExportButton = () => (
    <Button onClick={() => exportToXLSX(tableEventBuyersData)}>
      <FaRegFileExcel
        style={{ fontSize: "30px", color: "#04aa18", paddingTop: "4px" }}
      />
    </Button>
  );

  // above the Buyer column: refreshes only the dots, not the rest of the list
  const LiveStatusBar = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
        mb: 1,
        fontSize: "12px",
      }}
    >
      <Button
        size="small"
        variant="outlined"
        startIcon={<RefreshIcon />}
        disabled={checkingPresence}
        onClick={checkPresence}
        sx={{ textTransform: "none", whiteSpace: "nowrap" }}
      >
        {checkingPresence ? "Checking…" : "Refresh live status"}
      </Button>
      {Object.entries(PRESENCE_LOOK).map(([state, look]) => (
        <span key={state} style={{ display: "inline-flex", alignItems: "center" }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              marginRight: 6,
              backgroundColor: look.color,
            }}
          />
          {look.label}
        </span>
      ))}
    </Box>
  );

  return (
    <>
      <div className="AuctionTable">
        <h3>
          <Button sx={{ color: "#000" }} onClick={AuctionView}>
            <ArrowBackIcon />
          </Button>
          Buyer Event Summary
        </h3>
        <LiveStatusBar />
        <DataTableExtensions
          print={false}
          export={false}
          exportHeaders={false}
          columns={nextColumns}
          data={tableEventBuyersData}
        >
          <DataTable
            noHeader={false}
            defaultSortField="id"
            defaultSortAsc={false}
            pagination={false}
            highlightOnHover
            className="DataTable"
            actions={<ExportButton />}
          />
        </DataTableExtensions>
      </div>
    </>
  );
}

export default BidlogFirst;
